import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getFlavorInfoByName, getFlavors } from "./flavorSlice";
import { getOSs } from "./osSlice";
import { api_url, environment } from "../config.js";
import axios from "axios";

const fake_token =
  "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOi9hay5leGFtcGxlLmNvbS9hdXRoL3JlYWxttV7Y3cskztDZqqmQI";

const getAuthToken = () => {
  const keycloak = JSON.parse(localStorage.getItem("keycloak") || "{}");
  return environment === "production-no-auth" ? fake_token : keycloak?.token;
};

export const listTemplates = createAsyncThunk(
  "template/list",
  async (_, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.get(`${api_url}/templates`, config);
      return data;
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "An unexpected error occurred.",
      );
    }
  },
);

export const templateDetails = createAsyncThunk(
  "template/details",
  async (id, { dispatch, rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.get(`${api_url}/templates/${id}`, config);

      let flavorInfo = null;
      if (data?.flavorName) {
        const result = await dispatch(getFlavorInfoByName(data.flavorName));
        if (getFlavorInfoByName.fulfilled.match(result)) {
          flavorInfo = result.payload;
        }
      }

      return { template: data, flavor: flavorInfo };
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "An unexpected error occurred.",
      );
    }
  },
);

export const templateCreate = createAsyncThunk(
  "template/create",
  async (templateData, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.post(
        `${api_url}/templates`,
        templateData,
        config,
      );
      return data;
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "An unexpected error occurred.",
      );
    }
  },
);

export const templateUpdate = createAsyncThunk(
  "template/update",
  async ({ id, templateData }, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.put(
        `${api_url}/templates/${id}`,
        templateData,
        config,
      );
      return data;
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "An unexpected error occurred.",
      );
    }
  },
);

export const templateDelete = createAsyncThunk(
  "template/delete",
  async (id, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      await axios.delete(`${api_url}/templates/${id}`, config);
      return id; // so we can update the state after deletion
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "An unexpected error occurred.",
      );
    }
  },
);

export const listFlavors = createAsyncThunk(
  "flavor/list",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const resultAction = await dispatch(getFlavors());

      if (getFlavors.fulfilled.match(resultAction)) {
        return resultAction.payload;
      } else {
        return rejectWithValue(resultAction.payload || "Failed to get flavors");
      }
    } catch (error) {
      return rejectWithValue(error.message || "An unexpected error occurred.");
    }
  },
);

export const listOSs = createAsyncThunk(
  "os/list",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const resultAction = await dispatch(getOSs());

      if (getOSs.fulfilled.match(resultAction)) {
        return resultAction.payload;
      } else {
        return rejectWithValue(
          resultAction.payload || "Failed to get OS details",
        );
      }
    } catch (error) {
      return rejectWithValue(error.message || "An unexpected error occurred.");
    }
  },
);

export const templateSchema = createAsyncThunk(
  "template/templateSchema",
  async (_, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.get(`${api_url}/openapi.json`, config);
      const schema = data.components.schemas.Template;
      return schema;
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "An unexpected error occurred.",
      );
    }
  },
);

const initialState = {
  templates: [],
  template: {},
  template_schema: {},
  flavor: {},
  flavors: [],
  oss: [], // operating systems list
  loading: false,
  error: null,
};

const templateSlice = createSlice({
  name: "template",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // templates list
      .addCase(listTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listTemplates.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.templates || [];
      })

      .addCase(listTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // template details
      .addCase(templateDetails.pending, (state) => {
        state.loading = true;
        state.template = {};
        state.flavor = {};
        // state.error = null;
      })
      .addCase(templateDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.template = action.payload.template;
        state.flavor = action.payload.flavor;
      })
      .addCase(templateDetails.rejected, (state, action) => {
        state.loading = false;
        state.template = {};
        state.flavor = {};
        state.error = action.payload;
      })

      // create template
      .addCase(templateCreate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(templateCreate.fulfilled, (state, action) => {
        state.loading = false;
        state.templates.push(action.payload);
      })
      .addCase(templateCreate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // update template
      .addCase(templateUpdate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(templateUpdate.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = state.templates.map((template) =>
          template.id === action.payload.id ? action.payload : template,
        );
        state.template = action.payload;
      })
      .addCase(templateUpdate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // delete template
      .addCase(templateDelete.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(templateDelete.fulfilled, (state, action) => {
        state.loading = false;
        state.templates = state.templates.filter(
          (template) => template.id !== action.payload,
        );
        state.template = {};
      })
      .addCase(templateDelete.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // flavors list
      .addCase(listFlavors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listFlavors.fulfilled, (state, action) => {
        state.loading = false;
        state.flavors = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.flavors || [];
      })
      .addCase(listFlavors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // OSs list
      .addCase(listOSs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listOSs.fulfilled, (state, action) => {
        state.loading = false;
        state.oss = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.oss || [];
      })
      .addCase(listOSs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // template schema
      .addCase(templateSchema.pending, (state) => {
        state.loading = true;
      })
      .addCase(templateSchema.fulfilled, (state, action) => {
        state.loading = false;
        state.template_schema = action.payload;
      })
      .addCase(templateSchema.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export default templateSlice.reducer;

export { getFlavorInfoByName, getFlavors } from "./flavorSlice";
export { getOSs } from "./osSlice";
